/**
 * LandingFooter Component
 *
 * Blue footer with mountain overlay image.
 * Three columns: Logo + tagline, Resources, Follow.
 */
"use client";

import Image from "next/image";
import Link from "next/link";
import { trackFounderAppClicked, trackLandingFooterLinkClicked } from "@/lib/analytics/client";
import { FOUNDER_APPS } from "@/constants/founderApps";

export function LandingFooter() {
  const currentYearUtc = new Date().getUTCFullYear();
  return (
    <footer className="bg-landing-bg-light px-4 pb-4">
      <div className="relative bg-landing-blue text-white overflow-hidden rounded-3xl">
        {/* Pink mountains at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 md:h-48 pointer-events-none">
          <Image
            src="/landing/purple mountains.png"
            alt=""
            fill
            className="object-cover object-bottom"
            sizes="100vw"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1032px] mx-auto px-6 md:px-8 pt-12 md:pt-16 pb-36 md:pb-52">
          {/* Top section - Logo and Links */}
          <div className="flex flex-col md:flex-row justify-between gap-12 mb-20 md:mb-32">
            {/* Large Logo */}
            <div className="md:w-1/3">
              <Image
                src="/landing/lev-logo-white.svg"
                alt="Lev"
                width={160}
                height={64}
                className="w-32 md:w-40"
              />
            </div>

            {/* Links columns */}
            <div className="flex gap-16 md:gap-24">
              {/* Resources */}
              <div>
                <h4 className="font-medium mb-4">Resources</h4>
                <ul className="space-y-3">
                  {/* One list — see `@/constants/founderApps` for why. */}
                  {FOUNDER_APPS.map((app) => (
                    <li key={app.title}>
                      <a
                        href={app.href}
                        onClick={() => trackFounderAppClicked({ appName: app.title, location: "footer" })}
                        className="text-white/80 text-sm hover:text-white transition-colors"
                      >
                        {app.title}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a
                      href="/yc"
                      onClick={() => trackFounderAppClicked({ appName: "YC Application", location: "footer" })}
                      className="text-white/80 text-sm hover:text-white transition-colors"
                    >
                      YC Application
                    </a>
                  </li>
                  <li>
                    <Link
                      href="/venturegraph"
                      onClick={() => trackLandingFooterLinkClicked({ linkName: "Venture Graph", href: "/venturegraph" })}
                      className="text-white/80 text-sm hover:text-white transition-colors"
                    >
                      Venture Graph
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Follow */}
              <div>
                <h4 className="font-medium mb-4">Follow</h4>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="https://www.linkedin.com/company/trylev/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackLandingFooterLinkClicked({ linkName: "LinkedIn", href: "https://www.linkedin.com/company/trylev/" })}
                      className="text-white/80 text-sm hover:text-white transition-colors"
                    >
                      LinkedIn
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom section - Tagline, Copyright, Legal links */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            {/* Tagline */}
            <p className="text-white text-xl md:text-2xl font-medium leading-tight max-w-[280px]">
              Change the way you build your business
            </p>

            {/* Copyright */}
            <p className="text-white/80 text-sm" suppressHydrationWarning>
              All Rights Reserved {currentYearUtc}
            </p>

            {/* Legal links */}
            <div className="flex items-center gap-4 text-sm">
              <Link
                href="/privacy"
                onClick={() => trackLandingFooterLinkClicked({ linkName: "Privacy Policy", href: "/privacy" })}
                className="text-white/80 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <span aria-hidden="true" className="text-white/40">·</span>
              <Link
                href="/terms"
                onClick={() => trackLandingFooterLinkClicked({ linkName: "Terms of Service", href: "/terms" })}
                className="text-white/80 hover:text-white transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
